// src/routes/__root.tsx
/// <reference types="vite/client" />

import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
} from "@tanstack/react-router";
import * as React from "react";
import { QueryClient } from "@tanstack/react-query";
import { DefaultCatchBoundary } from "src/components/DefaultCatchBoundary";
import { seo } from "src/utils/seo";
import { Navbar } from "src/components/navbar/navbar";
import { TailwindIndicator } from "src/dev/tailwind-indicator";
import tailwindCss from "./tailwind.css?url";
import ckeditorCss from "./ckeditor.css?url";
import { Footer } from "src/components/footer/footer";
import { footerHeight, navbarHeight } from "../../tailwind.config";
import { cn } from "src/lib/utils";
import reactPhotoViewCss from "react-photo-view/dist/react-photo-view.css?url";
import { navbarQueryOptions } from "~/react-query/react-query";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { formDevtoolsPlugin } from "@tanstack/react-form-devtools";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { Toaster } from "src/components/ui/sonner";

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  loader: async ({ context }) => {
    const navbarData =
      await context.queryClient.ensureQueryData(navbarQueryOptions());
    return { navbarData };
  },
  head: () => {
    const title = "VESAS | Stavební firma z Třebíče";
    const description =
      "Jsme rodinná odborná stavební firma s více než 30letou tradicí. Zaměřujeme se na výstavbu a rekonstrukci rodinných domů a projekci a instalaci TZB.";
    const url = "https:/vesas.cz";
    const image = `${url}/og-image.png`;

    return {
      meta: [
        {
          charSet: "utf-8",
        },
        {
          name: "viewport",
          content: "width=device-width, initial-scale=1",
        },
        ...seo({
          title,
          description,
          url,
          image,
        }),
      ],
      links: [
        {
          rel: "apple-touch-icon",
          sizes: "180x180",
          href: "/apple-touch-icon.png",
        },
        {
          rel: "preload",
          href: "https://fonts.googleapis.com/css2?family=Gabarito:wght@400..900&display=swap",
          as: "font",
          crossOrigin: "anonymous",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "32x32",
          href: "/favicon-32x32.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "16x16",
          href: "/favicon-16x16.png",
        },
        { rel: "manifest", href: "/site.webmanifest", color: "#fffff" },
        { rel: "icon", href: "/favicon.ico" },
        { rel: "stylesheet", href: tailwindCss, type: "text/css" },
        {
          rel: "stylesheet",
          href: "https://cdn.ckeditor.com/ckeditor5/43.3.1/ckeditor5.css",
        },
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=Gabarito:wght@400..900&display=swap",
        },
        { rel: "stylesheet", href: ckeditorCss },
        { rel: "stylesheet", href: reactPhotoViewCss },
      ],
      scripts: [
        {
          defer: true,
          src: "https://umami.dominikjasek.cz/script.js",
          "data-website-id": "e2952bb0-b305-4e10-94d3-bcdbc59f8503",
        },
      ],
    };
  },
  errorComponent: (props) => {
    return <DefaultCatchBoundary {...props} />;
  },
  component: RootDocument,
});

function RootDocument() {
  const { navbarData } = Route.useLoaderData();

  return (
    <html>
      <head>
        <HeadContent />
      </head>
      <body className={"font-gabarito"}>
        <Scripts />

        {navbarData && <Navbar navbarItems={navbarData.nav_items} />}

        <div
          className={cn("flex flex-col justify-start pb-8")}
          style={{
            minHeight: `calc(100vh - ${navbarHeight}px - ${footerHeight}px)`,
          }}
        >
          <Outlet />
        </div>
        <Toaster position="top-center" />
        <TailwindIndicator />
        <Footer />
        <TanStackDevtools
          plugins={[
            formDevtoolsPlugin(),
            {
              name: "TanStack Query",
              render: <ReactQueryDevtoolsPanel />,
            },
            {
              name: "TanStack Router",
              render: <TanStackRouterDevtoolsPanel />,
            },
          ]}
        />
      </body>
    </html>
  );
}
